import type { Location } from "react-router-dom";
import { storedAdvertisingConsent } from "./consent";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/** Montant TTC en euros, devise et identifiant de déduplication d'une conversion. */
export interface ConversionData {
  value?: number;
  currency?: string;
  transaction_id?: string;
}

interface ConversionResponse {
  transaction_id?: string;
  transaction_value?: number;
  transaction_currency?: string;
}

/**
 * Pousse un évènement dans le dataLayer GTM. `user_data` alimente les conversions
 * améliorées de Google Ads (hachage côté GTM).
 */
export function trackEvent(
  event: string,
  location: Location,
  data: ConversionData & {
    user_data?: { email?: string | null };
    [key: string]: unknown;
  } = {},
) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    page: {
      path: location.pathname,
      search: location.search,
      title: document.title,
    },
    ...data,
  });
}

export function sendPageView(location: Location) {
  trackEvent("pageview", location);
}

const PRIVATE_PARAMS = ["mail"];

/**
 * Paramètres d'URL personnels (adresse email du lien de confirmation) à déplacer dans le state
 * de navigation avant qu'une page vue ne parte aux outils de mesure. `null` si l'URL est propre.
 */
export function withoutPrivateParams(
  location: Location,
): { search: string; state: Record<string, string> } | null {
  const params = new URLSearchParams(location.search);
  const state: Record<string, string> = {};
  for (const name of PRIVATE_PARAMS) {
    const value = params.get(name);
    if (value !== null) {
      state[name] = value;
      params.delete(name);
    }
  }
  if (Object.keys(state).length === 0) return null;
  const search = params.toString();
  return { search: search ? `?${search}` : "", state };
}

const OPPREF_COOKIE = "__oppref=";

/**
 * Identifiant de clic ChatGPT Ads : paramètre `oppref` de l'URL d'arrivée, sinon cookie
 * `__oppref` posé sur .qalimo.fr par le pixel OpenAI (seulement avec le consentement publicitaire).
 */
export function getOppref(searchParams: URLSearchParams): string | undefined {
  const fromUrl = searchParams.get("oppref")?.trim();
  if (fromUrl) return fromUrl;
  const cookie = document.cookie
    .split("; ")
    .find((c) => c.startsWith(OPPREF_COOKIE));
  return cookie
    ? decodeURIComponent(cookie.slice(OPPREF_COOKIE.length)).trim() || undefined
    : undefined;
}

/**
 * Champs d'attribution joints à la création de compte : le serveur s'en sert pour envoyer
 * les conversions à l'API Conversions OpenAI, qui ne lit ni cookie ni bannière.
 */
export function signupTrackingFields(searchParams: URLSearchParams): {
  openai_oppref?: string;
  advertising_consent: boolean | null;
} {
  return {
    openai_oppref: getOppref(searchParams),
    advertising_consent: storedAdvertisingConsent(),
  };
}

export function conversionData(response: ConversionResponse): ConversionData {
  return {
    value: response.transaction_value,
    currency: response.transaction_currency,
    transaction_id: response.transaction_id,
  };
}
